import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import {
  AppBar,
  Box,
  Button,
  Drawer,
  makeStyles,
  Toolbar,
  Typography,
  IconButton,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@material-ui/core';

import SettingsIcon from '@material-ui/icons/Settings';
import CachedIcon from '@material-ui/icons/Cached';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';

import { DepartmentSelect } from '../components/DepartmentSelect';
import { StackTable } from '../components/StackTable';
import { ApplicationState } from '../store';

import { RoutePaths } from '../Routes';
import { authActions } from '../store/auth/actions';
import { stackActions } from '../store/stack/actions';
import { MessageSnackbar } from '../components/MessageSnackbar';
import { buttonInactiveColor, warningColor } from '../theme/theme';
import { globalActions } from '../store/actions';

const useStyles = makeStyles((theme) => ({
  rootBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  appBar: {
    padding: 0,
    height: '5rem',
    marginBottom: '5rem',
  },
  toolbar: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    height: '100%',
  },
  menuContainer: {
    boxShadow:
      '0px -3px 1px -2px rgb(0 0 0 / 20%), 0px -2px 2px 0px rgb(0 0 0 / 14%), 0px -1px 5px 0px rgb(0 0 0 / 12%)',
    padding: 0,
    flexGrow: 1,
    marginRight: '1.4rem',
    height: '100%',
    borderRadius: 0,
    background: 'none',
    overflow: 'hidden',
  },
  drawer: {
    display: 'flex',
  },
  version: {
    fontSize: '0.8rem',
    textAlign: 'right',
    color: buttonInactiveColor(),
  },
  flexGrow: {
    flexGrow: 1,
  },
  topMargin: {
    marginTop: '5rem',
  },
  registrations: {
    paddingTop: '0.1rem',
    paddingBottom: '0.1rem',
    backgroundColor: '#d9dce1',
  },
  offline: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  offlineIcon: {
    color: warningColor,
    fontSize: '5rem',
    margin: theme.spacing(1),
  },
  offlineSmall: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  offlineIconSmall: {
    color: warningColor,
    fontSize: '2.4rem',
    margin: theme.spacing(1),
  },
  buttonBox: {
    margin: '20px 0px',
    paddingBottom: `env(safe-area-inset-bottom)`,
    position: 'fixed',
    bottom: '0px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  navIcon: {
    color: '#fff',
  },
  snackbar: {
    bottom: '80px',
    marginBottom: `env(safe-area-inset-bottom)`,
  },
  bold: {
    fontWeight: 'bold',
  },
  tableContainer: {
    height: '1rem', // need to assign some height for flexGrow to kick in
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    width: '100%',
  },
  preDataView: {
    paddingTop: '14rem',
    textAlign: 'center',
  },
}));

const Overview = (): React.ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [isLoading, setLoading] = useState(false);
  const measurementCard = useSelector((state: ApplicationState) => {
    const department = state.user.selectedDepartment;

    if (!department) {
      return undefined;
    }

    return state.stacks.departments[department.id]?.measurementCard;
  });
  const { t } = useTranslation(['translations']);

  const [isSettingsEnabled, setSettingsEnabled] = useState(false);

  const toggleSettingsEnabled = () => {
    setSettingsEnabled(!isSettingsEnabled);
  };

  const logoutClicked = () => {
    dispatch(authActions.logout());
  };

  const classes = useStyles();

  const handleSync = async () => {
    setLoading(true);
    await dispatch(globalActions.syncAllData());
    setLoading(false);
  };

  const { departmentId, departmentStacks } = useSelector(
    (state: ApplicationState) => {
      if (!state.user.selectedDepartment) {
        return {};
      }

      return {
        departmentId: state.user.selectedDepartment.id,
        departmentStacks:
          state.stacks.departments[state.user.selectedDepartment.id],
      };
    },
  );

  const registerClicked = () => {
    if (departmentId) {
      dispatch(stackActions.clearFieldValues(departmentId));
    }
    history.push(RoutePaths.Registration);
  };

  useEffect(() => {
    if (departmentId) {
      dispatch(stackActions.getStacksByDepartmentId(departmentId));
    }
  }, [departmentId]);

  const [isOffline, setOffline] = useState(!navigator.onLine);
  const [smallOfflineBox, setSmallOfflineBox] = useState(false);

  useEffect(() => {
    function handleOnline() {
      setOffline(false);
    }
    function handleOffline() {
      setOffline(true);
      setSmallOfflineBox(false);
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return function cleanupListener() {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  let offlineBox = null;
  if (isOffline) {
    if (smallOfflineBox) {
      offlineBox = (
        <Box
          className={classes.offlineSmall}
          onClick={() => {
            setSmallOfflineBox(false);
          }}
        >
          <ReportProblemIcon className={classes.offlineIconSmall} />
          <Typography variant="h2" className={classes.bold}>
            {t('overview_page.offline_title')}
          </Typography>
        </Box>
      );
    } else {
      offlineBox = (
        <Box
          className={classes.offline}
          onClick={() => {
            setSmallOfflineBox(true);
          }}
        >
          <ReportProblemIcon className={classes.offlineIcon} />
          <Typography variant="h2" className={classes.bold} gutterBottom>
            {t('overview_page.offline_title')}
          </Typography>
          <Box> {t('overview_page.offline_desc')}</Box>
        </Box>
      );
    }
  }

  return (
    <Box className={classes.rootBox}>
      <AppBar className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <span className={classes.menuContainer}>
            <DepartmentSelect />
          </span>
          {isLoading ? (
            <IconButton
              className={classes.navIcon}
              onClick={handleSync}
              disabled
            >
              <CircularProgress className={classes.navIcon} size="1.5rem" />
            </IconButton>
          ) : (
            <IconButton className={classes.navIcon} onClick={handleSync}>
              <CachedIcon />
            </IconButton>
          )}
          <IconButton
            className={classes.navIcon}
            onClick={toggleSettingsEnabled}
          >
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box className={classes.topMargin} />
      {offlineBox}
      <Box className={classes.registrations}>
        <ListItem>
          <ListItemText>
            <Typography variant="h2">
              {t('overview_page.registrations')}
            </Typography>
            {!!departmentStacks?.lastUpdated && (
              <Typography variant="h4">
                {t('overview_page.last_updated')}{' '}
                {new Date(departmentStacks?.lastUpdated).toLocaleDateString(
                  navigator.language,
                )}
              </Typography>
            )}
          </ListItemText>
        </ListItem>
      </Box>
      <Box
        className={classes.tableContainer}
        onClick={() => {
          setSmallOfflineBox(true);
        }}
      >
        {departmentId ? (
          measurementCard ? (
            <StackTable />
          ) : (
            <div className={classes.preDataView}>
              {t('overview_page.no_measurement_card')}
            </div>
          )
        ) : (
          <div className={classes.preDataView}>
            {t('overview_page.no_departments')}
          </div>
        )}
      </Box>
      <MessageSnackbar className={classes.snackbar} />
      <Box className={classes.buttonBox}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          onClick={registerClicked}
          disabled={!measurementCard}
        >
          {t('overview_page.register_button')}
        </Button>
      </Box>
      <Drawer
        anchor="right"
        open={isSettingsEnabled}
        onClose={toggleSettingsEnabled}
        className={classes.drawer}
      >
        <Button variant="contained" color="primary" onClick={logoutClicked}>
          Logout
        </Button>
        <Box className={classes.flexGrow}></Box>
        <Typography className={classes.version}>
          v. {process.env.REACT_APP_VERSION}
        </Typography>
      </Drawer>
    </Box>
  );
};

export default Overview;
